import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

import {
  type GetEligibleCreditReversalOrdersResponse,
  getEligibleCreditReversals,
  getPurchases,
} from '~/services/OrdersService';

export interface EligibleCreditReversalSearch {
  brand?: string;
  bookingNumber?: string;
  orderId?: string;
}

export const creditReversalQueryKeys = {
  all: ['creditReversals'] as const,
  lists: () => [...creditReversalQueryKeys.all, 'list'] as const,
  infinite: (userId: string, limit: number, search?: EligibleCreditReversalSearch) =>
    [
      ...creditReversalQueryKeys.lists(),
      userId,
      limit,
      { bookingNumber: search?.bookingNumber ?? null, brand: search?.brand ?? null, orderId: search?.orderId ?? null },
    ] as const,
};

const DEFAULT_LIMIT = 10;

function buildEmptySearchResponse(limit: number): GetEligibleCreditReversalOrdersResponse {
  return {
    status: 200,
    message: '',
    result: [],
    hasMore: false,
    nextOrderOffset: 0,
    limit,
  };
}

async function getEligibleCreditReversalsForSearch(
  userId: string,
  limit: number,
  orderOffset: number,
  search: EligibleCreditReversalSearch | undefined,
  signal?: AbortSignal,
): Promise<GetEligibleCreditReversalOrdersResponse> {
  const orderId = search?.orderId?.trim();
  const bookingNumber = search?.bookingNumber?.trim();

  if (!orderId && !bookingNumber) {
    return getEligibleCreditReversals(userId, limit, orderOffset, undefined, signal);
  }

  if (orderId) {
    return getEligibleCreditReversals(userId, limit, orderOffset, orderId, signal);
  }

  if (!search?.brand) {
    return buildEmptySearchResponse(limit);
  }

  const purchases = await getPurchases({
    page: 1,
    per_page: limit,
    brand: search.brand,
    customer_id: userId,
    filterBy: 'booking_numbers',
    filterValue: bookingNumber,
    signal,
  });
  const orderIds: Array<string> = Array.from(
    new Set(
      (purchases.result ?? [])
        .map((order) => order.id_orders || order.id)
        .filter((orderId): orderId is string => Boolean(orderId)),
    ),
  );

  if (orderIds.length === 0) {
    return buildEmptySearchResponse(limit);
  }

  const responses = await Promise.all(
    orderIds.map((orderId) => getEligibleCreditReversals(userId, 1, 0, orderId, signal)),
  );
  const result = Array.from(
    new Map(responses.flatMap((response) => response.result).map((row) => [row.orderId, row])).values(),
  );

  return {
    ...responses[0],
    result,
    hasMore: false,
    nextOrderOffset: 0,
  };
}

export function useGetEligibleCreditReversals(
  userId: string,
  limit = DEFAULT_LIMIT,
  enabled = true,
  search?: EligibleCreditReversalSearch,
) {
  const hasSearch = Boolean(search?.orderId?.trim() || search?.bookingNumber?.trim());

  return useInfiniteQuery<
    GetEligibleCreditReversalOrdersResponse,
    Error,
    InfiniteData<GetEligibleCreditReversalOrdersResponse, number>,
    ReadonlyArray<unknown>,
    number
  >({
    queryKey: creditReversalQueryKeys.infinite(userId, limit, search),
    queryFn: ({ pageParam, signal }) => getEligibleCreditReversalsForSearch(userId, limit, pageParam, search, signal),
    getNextPageParam: (lastPage) => (!hasSearch && lastPage.hasMore ? lastPage.nextOrderOffset : undefined),
    initialPageParam: 0,
    enabled: !!userId && enabled,
  });
}
